import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { refresh } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      setError("Missing session id. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/session",
          { session_id: sessionId },
          { withCredentials: true }
        );
        // Pull the FULL user (including mfa_enrolled + mfa_verified) so ProtectedRoute
        // knows whether to send us to /mfa or straight to the dashboard.
        const me = await refresh();
        // Strip the hash so a refresh doesn't re-run
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${data.name || data.email}`);
        // If MFA is required (not verified yet), the ProtectedRoute on "/" will bounce to /mfa.
        // We navigate straight to /mfa when unverified to skip the double-redirect flash.
        const target = me?.mfa_verified ? "/" : "/mfa";
        navigate(target, { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed.";
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen bg-[#f8fafc] text-[#0f172a] flex items-center justify-center px-6"
      data-testid="auth-callback"
    >
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h2 className="font-heading text-xl font-semibold text-[#b94a48]">Sign-in problem</h2>
            <p className="text-sm text-[#475569] mt-2">{error}</p>
            <button
              onClick={() => (window.location.href = "/login")}
              className="mt-6 px-4 py-2 rounded-md bg-[#0f172a] text-white text-sm"
              data-testid="auth-callback-back-to-login"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h2 className="font-heading text-xl font-semibold">Signing you in…</h2>
            <p className="text-sm text-[#475569] mt-2">One moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
